import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Sparkles, Trash2, Pencil, Upload, Image as ImageIcon, Loader2, ArrowLeft, ArrowRight, Link2 } from "lucide-react";

const inputCls = "w-full px-3 py-2 border border-[var(--line)] focus:border-[var(--accent)] outline-none text-sm";

async function uploadImage(file) {
  const fd = new FormData(); fd.append("file", file);
  const { data } = await api.post("/media/image?folder=uploads/", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

function ImagePicker({ label, value, onChange, testid, aspect = "aspect-video w-40" }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try { onChange(await uploadImage(file)); toast.success("Image uploaded"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div>
      <label className="text-xs font-semibold text-[var(--ink-2)] mb-1.5 block">{label}</label>
      <div className="flex items-center gap-3">
        <div className={`${aspect} border border-[var(--line)] bg-[var(--alt)] overflow-hidden flex items-center justify-center shrink-0`}>
          {value ? <img src={value} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={20} className="text-[var(--ink-2)]" />}
        </div>
        <label data-testid={testid} className="cursor-pointer text-sm font-semibold border border-[var(--line)] px-4 py-2 hover:bg-[var(--alt)]">
          {busy ? "Uploading…" : value ? "Change" : "Upload"}
          <input type="file" accept="image/*" className="hidden" disabled={busy} onChange={pick} />
        </label>
      </div>
    </div>
  );
}

/* ----------------------------- News ----------------------------- */
function NewsDialog({ item, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const empty = { title: "", excerpt: "", body: "", cover_url: "", cover_video: "", inline_image_url: "", published: false };
  const [f, setF] = useState(item ? { ...empty, ...item } : empty);
  const [topic, setTopic] = useState("");
  const [generating, setGenerating] = useState(false);
  const [vidBusy, setVidBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setVidBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/admin/content/upload-video", fd, { headers: { "Content-Type": "multipart/form-data" } }); set("cover_video", data.video_url); toast.success("Video uploaded"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Video upload failed"); }
    finally { setVidBusy(false); e.target.value = ""; }
  };

  const generate = async () => {
    if (!topic.trim()) { toast.error("Enter a topic or headline for the AI"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/news/generate", { topic });
      setF((p) => ({ ...p, title: data.title || p.title, excerpt: data.excerpt || p.excerpt, body: data.body || p.body }));
      toast.success("Draft generated — review and edit before publishing");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Generation failed"); }
    finally { setGenerating(false); }
  };

  const save = async (publish) => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const body = { ...f, published: publish ?? f.published };
      if (item) await api.put(`/admin/news/${item.id}`, body);
      else await api.post("/admin/news", body);
      toast.success(item ? "Saved" : "Created");
      setOpen(false); onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="rounded-none max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{item ? "Edit news post" : "New news post"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="border border-[var(--line)] bg-[var(--alt)] p-3">
            <label className="text-xs font-semibold text-[var(--ink-2)] mb-1.5 flex items-center gap-1.5"><Sparkles size={13} className="text-[var(--accent)]" /> AI draft assistant</label>
            <div className="flex gap-2">
              <input data-testid="news-ai-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. We just launched a new AI bootcamp" className={inputCls} />
              <button onClick={generate} disabled={generating} data-testid="news-ai-generate" className="shrink-0 inline-flex items-center gap-1.5 bg-[var(--ink)] text-white font-semibold px-4 text-sm hover:bg-[var(--accent)] disabled:opacity-60">
                {generating ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Generate
              </button>
            </div>
          </div>
          <input data-testid="news-title" placeholder="Title" value={f.title} onChange={(e) => set("title", e.target.value)} className={inputCls} />
          <textarea data-testid="news-excerpt" placeholder="Short excerpt (shown on cards)" value={f.excerpt} onChange={(e) => set("excerpt", e.target.value)} rows={2} className={inputCls} />
          <textarea data-testid="news-body" placeholder="Article body" value={f.body} onChange={(e) => set("body", e.target.value)} rows={8} className={inputCls} />
          <ImagePicker label="Cover image" value={f.cover_url} onChange={(v) => set("cover_url", v)} testid="news-cover-upload" />
          <div>
            <label className="text-xs font-semibold text-[var(--ink-2)] mb-1.5 block">Feature video (optional) — shows at the top instead of the cover image</label>
            <input data-testid="news-video-url" placeholder="Paste a video URL, or upload below" value={f.cover_video} onChange={(e) => set("cover_video", e.target.value)} className={inputCls} />
            <label data-testid="news-video-upload" className="mt-2 cursor-pointer inline-flex items-center gap-2 text-sm font-semibold border border-[var(--line)] px-4 py-2 hover:bg-[var(--alt)]">
              <Upload size={15} /> {vidBusy ? "Uploading…" : "Upload video file"}
              <input type="file" accept="video/*" className="hidden" disabled={vidBusy} onChange={uploadVideo} />
            </label>
          </div>
          <ImagePicker label="In-article image (optional) — placed in the middle of the article" value={f.inline_image_url} onChange={(v) => set("inline_image_url", v)} testid="news-inline-upload" />
        </div>
        <DialogFooter className="gap-2">
          <button onClick={() => save(false)} disabled={saving} data-testid="news-save-draft" className="border border-[var(--ink)] font-semibold px-5 py-2.5 hover:bg-[var(--ink)] hover:text-white text-sm disabled:opacity-60">Save as draft</button>
          <button onClick={() => save(true)} disabled={saving} data-testid="news-publish" className="bg-[var(--accent)] text-white font-semibold px-5 py-2.5 hover:bg-[var(--accent-hover)] text-sm disabled:opacity-60">{saving ? "Saving…" : "Publish"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewsManager() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/news").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => { if (!window.confirm("Delete this post?")) return; await api.delete(`/admin/news/${id}`); toast.success("Deleted"); load(); };
  return (
    <div data-testid="news-manager">
      <div className="flex justify-end mb-3"><NewsDialog onSaved={load} trigger={<button data-testid="news-new" className="inline-flex items-center gap-2 bg-[var(--accent)] text-white font-semibold px-4 py-2.5 hover:bg-[var(--accent-hover)] text-sm"><Plus size={16} /> New post</button>} /></div>
      <div className="border border-[var(--line)] bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="news-table">
          <thead className="bg-[var(--alt)] text-left text-xs uppercase tracking-wide text-[var(--ink-2)]"><tr><th className="px-4 py-3">Title</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr></thead>
          <tbody>
            {items.map((n) => (
              <tr key={n.id} className="border-t border-[var(--line)]" data-testid={`news-row-${n.id}`}>
                <td className="px-4 py-3"><div className="font-semibold">{n.title}</div><div className="text-xs text-[var(--ink-2)]">{n.excerpt}</div></td>
                <td className="px-4 py-3">{n.published ? <span className="text-[#1B5E20] font-semibold">Published</span> : <span className="text-[var(--ink-2)]">Draft</span>}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <NewsDialog item={n} onSaved={load} trigger={<button data-testid={`news-edit-${n.id}`} className="text-[var(--accent)] hover:underline inline-flex items-center gap-1 mr-3"><Pencil size={14} /> Edit</button>} />
                  <button onClick={() => remove(n.id)} className="text-[#B71C1C] hover:underline inline-flex items-center gap-1"><Trash2 size={14} /> Delete</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={3} className="px-4 py-10 text-center text-[var(--ink-2)]">No posts yet. Create your first update.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ----------------------------- Testimonials ----------------------------- */
function TestimonialDialog({ item, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const empty = { name: "", role: "", quote: "", photo_url: "", video_url: "", published: true, order: 0 };
  const [f, setF] = useState(item ? { ...empty, ...item } : empty);
  const [vidBusy, setVidBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setVidBusy(true);
    try { const { data } = await api.post("/admin/content/upload-video", (() => { const fd = new FormData(); fd.append("file", file); return fd; })(), { headers: { "Content-Type": "multipart/form-data" } }); set("video_url", data.video_url); toast.success("Video uploaded"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Video upload failed"); }
    finally { setVidBusy(false); e.target.value = ""; }
  };

  const save = async () => {
    if (!f.name.trim() || !(f.quote.trim() || f.video_url.trim())) { toast.error("Name and either a quote or a video are required"); return; }
    setSaving(true);
    try {
      if (item) await api.put(`/admin/testimonials/${item.id}`, f);
      else await api.post("/admin/testimonials", f);
      toast.success(item ? "Saved" : "Added"); setOpen(false); onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="rounded-none max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{item ? "Edit testimonial" : "New testimonial"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input data-testid="t-name" placeholder="Name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
          <input data-testid="t-role" placeholder="Role / course (e.g. Hook & Latch graduate)" value={f.role} onChange={(e) => set("role", e.target.value)} className={inputCls} />
          <textarea data-testid="t-quote" placeholder="Quote" value={f.quote} onChange={(e) => set("quote", e.target.value)} rows={3} className={inputCls} />
          <ImagePicker label="Photo (optional)" value={f.photo_url} onChange={(v) => set("photo_url", v)} testid="t-photo-upload" aspect="w-20 h-20" />
          <div>
            <label className="text-xs font-semibold text-[var(--ink-2)] mb-1.5 block">Video (optional) — paste a YouTube/Vimeo link, or upload a file</label>
            <input data-testid="t-video-url" placeholder="https://youtube.com/watch?v=…" value={f.video_url} onChange={(e) => set("video_url", e.target.value)} className={inputCls} />
            <label data-testid="t-video-upload" className="mt-2 cursor-pointer inline-flex items-center gap-2 text-sm font-semibold border border-[var(--line)] px-4 py-2 hover:bg-[var(--alt)]">
              <Upload size={15} /> {vidBusy ? "Uploading…" : "Upload video file"}
              <input type="file" accept="video/*" className="hidden" disabled={vidBusy} onChange={uploadVideo} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3 items-center">
            <input data-testid="t-order" type="number" placeholder="Order" value={f.order} onChange={(e) => set("order", parseInt(e.target.value || 0, 10))} className={inputCls} />
            <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" data-testid="t-published" checked={f.published} onChange={(e) => set("published", e.target.checked)} className="accent-[var(--accent)]" /> Published</label>
          </div>
        </div>
        <DialogFooter><button onClick={save} disabled={saving} data-testid="t-save" className="bg-[var(--accent)] text-white font-semibold px-5 py-2.5 hover:bg-[var(--accent-hover)] w-full disabled:opacity-60">{saving ? "Saving…" : "Save"}</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TestimonialsManager() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => { if (!window.confirm("Delete this testimonial?")) return; await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); load(); };
  return (
    <div data-testid="testimonials-manager">
      <div className="flex justify-end mb-3"><TestimonialDialog onSaved={load} trigger={<button data-testid="testimonial-new" className="inline-flex items-center gap-2 bg-[var(--accent)] text-white font-semibold px-4 py-2.5 hover:bg-[var(--accent-hover)] text-sm"><Plus size={16} /> New testimonial</button>} /></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((t) => (
          <div key={t.id} className="border border-[var(--line)] bg-white p-4" data-testid={`testimonial-row-${t.id}`}>
            <div className="flex items-center gap-3 mb-2">
              {t.photo_url ? <img src={t.photo_url} alt="" className="w-10 h-10 object-cover border border-[var(--line)]" /> : <div className="w-10 h-10 bg-[var(--alt)] border border-[var(--line)]" />}
              <div><div className="font-semibold text-sm">{t.name}</div><div className="text-xs text-[var(--ink-2)]">{t.role}</div></div>
            </div>
            <p className="text-sm text-[var(--ink-2)] line-clamp-3">{t.quote || (t.video_url ? "🎬 Video testimonial" : "")}</p>
            <div className="flex items-center justify-between mt-3 text-sm">
              <span className={t.published ? "text-[#1B5E20] font-semibold" : "text-[var(--ink-2)]"}>{t.published ? "Published" : "Hidden"}</span>
              <span className="whitespace-nowrap">
                <TestimonialDialog item={t} onSaved={load} trigger={<button data-testid={`testimonial-edit-${t.id}`} className="text-[var(--accent)] hover:underline mr-3">Edit</button>} />
                <button onClick={() => remove(t.id)} className="text-[#B71C1C] hover:underline">Delete</button>
              </span>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full border border-[var(--line)] bg-white py-10 text-center text-[var(--ink-2)]">No testimonials yet.</div>}
      </div>
    </div>
  );
}

/* ----------------------------- Gallery ----------------------------- */
function GalleryDialog({ item, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const empty = { image_url: "", video_url: "", caption: "", link_url: "", published: true, order: 0 };
  const [f, setF] = useState(item ? { ...empty, ...item } : empty);
  const [saving, setSaving] = useState(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const onVideo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setVideoUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/content/upload-video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("video_url", data.video_url); toast.success("Video uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Video upload failed"); }
    finally { setVideoUploading(false); e.target.value = ""; }
  };
  const save = async () => {
    if (!f.image_url && !f.video_url) { toast.error("Please upload an image or a video"); return; }
    setSaving(true);
    try {
      if (item) await api.put(`/admin/gallery/${item.id}`, f);
      else await api.post("/admin/gallery", f);
      toast.success(item ? "Saved" : "Added"); setOpen(false); onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="rounded-none max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{item ? "Edit item" : "Add image or video"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <ImagePicker label="Image (or cover image for a video)" value={f.image_url} onChange={(v) => set("image_url", v)} testid="g-image-upload" aspect="aspect-video w-48" />
          <div>
            <label className="text-xs font-semibold text-[var(--ink-2)] mb-1.5 block">Video (optional — YouTube/Vimeo link or upload)</label>
            <input data-testid="g-video" placeholder="https://youtube.com/watch?v=…" value={f.video_url} onChange={(e) => set("video_url", e.target.value)} className={inputCls} />
            <div className="flex items-center gap-2 mt-2">
              <label data-testid="g-video-upload" className="cursor-pointer inline-flex items-center gap-2 text-sm font-semibold border border-[var(--line)] px-4 py-2 hover:bg-[var(--alt)]">
                <Upload size={15} /> {videoUploading ? "Uploading…" : "Upload video (max 50 MB)"}
                <input type="file" accept="video/*" className="hidden" disabled={videoUploading} onChange={onVideo} />
              </label>
              {f.video_url && <button type="button" onClick={() => set("video_url", "")} className="text-xs text-[#B71C1C] hover:underline">Remove video</button>}
            </div>
          </div>
          <input data-testid="g-caption" placeholder="Caption (optional)" value={f.caption} onChange={(e) => set("caption", e.target.value)} className={inputCls} />
          <input data-testid="g-link" placeholder="Link URL (optional)" value={f.link_url} onChange={(e) => set("link_url", e.target.value)} className={inputCls} />
          <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" data-testid="g-published" checked={f.published} onChange={(e) => set("published", e.target.checked)} className="accent-[var(--accent)]" /> Published (visible on the homepage)</label>
        </div>
        <DialogFooter><button onClick={save} disabled={saving} data-testid="g-save" className="bg-[var(--accent)] text-white font-semibold px-5 py-2.5 hover:bg-[var(--accent-hover)] w-full disabled:opacity-60">{saving ? "Saving…" : "Save"}</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GalleryManager() {
  const [items, setItems] = useState([]);
  const [reordering, setReordering] = useState(false);
  const load = useCallback(() => api.get("/admin/gallery").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const remove = async (id) => { if (!window.confirm("Delete this image?")) return; await api.delete(`/admin/gallery/${id}`); toast.success("Deleted"); load(); };
  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= items.length || reordering) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    setItems(next);
    setReordering(true);
    try {
      await api.put("/admin/gallery/reorder", { ids: next.map((g) => g.id) });
    } catch { toast.error("Could not save order"); load(); }
    finally { setReordering(false); }
  };
  return (
    <div data-testid="gallery-manager">
      <p className="text-sm text-[var(--ink-2)] mb-3">These images power the homepage <strong>"What our learners say"</strong> carousel (4 shown at a time, scrolls horizontally). Upload graduate photos, adverts or sale banners; add an optional link (e.g. to a course or the store), then use the <ArrowLeft size={12} className="inline" /> <ArrowRight size={12} className="inline" /> arrows to arrange the order.</p>
      <div className="flex justify-end mb-3"><GalleryDialog onSaved={load} trigger={<button data-testid="gallery-new" className="inline-flex items-center gap-2 bg-[var(--accent)] text-white font-semibold px-4 py-2.5 hover:bg-[var(--accent-hover)] text-sm"><Plus size={16} /> Add image</button>} /></div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((g, i) => (
          <div key={g.id} className="border border-[var(--line)] bg-white" data-testid={`gallery-row-${g.id}`}>
            <div className="relative">
              {g.image_url
                ? <img src={g.image_url} alt={g.caption} className="w-full aspect-video object-cover" />
                : <video src={g.video_url} className="w-full aspect-video object-cover bg-black" muted />}
              <span className="absolute top-1.5 left-1.5 bg-[var(--ink)]/80 text-white text-[10px] font-semibold px-1.5 py-0.5">#{i + 1}</span>
              {g.video_url && <span className="absolute top-1.5 left-9 bg-[var(--accent)] text-white text-[10px] font-semibold px-1.5 py-0.5">VIDEO</span>}
              {g.link_url && <span className="absolute top-1.5 right-1.5 bg-[var(--accent)] text-white p-1" title={g.link_url}><Link2 size={12} /></span>}
              <div className="absolute bottom-1.5 left-1.5 right-1.5 flex justify-between">
                <button onClick={() => move(i, -1)} disabled={i === 0 || reordering} data-testid={`gallery-move-left-${g.id}`} className="bg-white/90 hover:bg-white text-[var(--ink)] p-1 disabled:opacity-30" title="Move left"><ArrowLeft size={14} /></button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1 || reordering} data-testid={`gallery-move-right-${g.id}`} className="bg-white/90 hover:bg-white text-[var(--ink)] p-1 disabled:opacity-30" title="Move right"><ArrowRight size={14} /></button>
              </div>
            </div>
            <div className="p-3 text-sm">
              <div className="truncate text-[var(--ink-2)]">{g.caption || "—"}</div>
              <div className="flex items-center justify-between mt-2">
                <span className={g.published ? "text-[#1B5E20] font-semibold text-xs" : "text-[var(--ink-2)] text-xs"}>{g.published ? "Visible" : "Hidden"}</span>
                <span className="whitespace-nowrap">
                  <GalleryDialog item={g} onSaved={load} trigger={<button data-testid={`gallery-edit-${g.id}`} className="text-[var(--accent)] hover:underline mr-2 text-xs">Edit</button>} />
                  <button onClick={() => remove(g.id)} className="text-[#B71C1C] hover:underline text-xs">Delete</button>
                </span>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full border border-[var(--line)] bg-white py-10 text-center text-[var(--ink-2)]">No images yet. Add images for the homepage carousel.</div>}
      </div>
    </div>
  );
}

export function ContentManager() {
  return (
    <div data-testid="content-manager">
      <Tabs defaultValue="news">
        <TabsList className="rounded-none bg-[var(--alt)]">
          <TabsTrigger value="news" data-testid="ctab-news" className="rounded-none data-[state=active]:bg-white">Latest news</TabsTrigger>
          <TabsTrigger value="testimonials" data-testid="ctab-testimonials" className="rounded-none data-[state=active]:bg-white">Testimonials</TabsTrigger>
          <TabsTrigger value="gallery" data-testid="ctab-gallery" className="rounded-none data-[state=active]:bg-white">Learner showcase</TabsTrigger>
        </TabsList>
        <TabsContent value="news" className="mt-5"><NewsManager /></TabsContent>
        <TabsContent value="testimonials" className="mt-5"><TestimonialsManager /></TabsContent>
        <TabsContent value="gallery" className="mt-5"><GalleryManager /></TabsContent>
      </Tabs>
    </div>
  );
}
